import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRouteSnapshot, Route, RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { MsalGuard, MsalRedirectComponent } from '@azure/msal-angular';
import { AfterLogoutComponent } from './auth/components/after-logout/after-logout.component';
import { AuthorizationErrorComponent } from './auth/components/authorization-error/authorization-error.component';
import { LogoutComponent } from './auth/components/logout/logout.component';
import { accessGuard, IAccessGuardData } from './auth/guards/access/access.guard';
import { organizationContextGuard } from './auth/guards/organization-context/organization-context.guard';
import { FEATURES } from './auth/models/features';
import { PERMISSIONS } from './auth/models/permissions';
import { DashboardType } from './dashboards/models/enums/dashboard-type';
import { TitleService } from './dashboards/services/title.service';
import { IHierarchyMenuConfig } from './shared/components/menu/hierarchy-menu/hierarchy-menu.component';
import { CasePipe } from './shared/pipes/case/case.pipe';
import { IQueryParametersConfig } from './shared/services/query-parameters/query-parameters.service';
import { UnsavedChangesGuard } from './shared/services/unsaved-changes/unsaved-changes.component';

export const routes: AppRoutes = [
  { path: 'auth', component: MsalRedirectComponent },
  { path: 'logout', component: LogoutComponent },
  { path: 'after-b2c-logout', component: AfterLogoutComponent },
  {
    path: '',
    canActivate: [MsalGuard],
    canDeactivate: [UnsavedChangesGuard],
    children: [
      { path: '403', component: AuthorizationErrorComponent },
      {
        path: '',
        title: 'Energie',
        loadChildren: () => import('./dashboards/dashboards.routes'),
        canActivate: [accessGuard],
        data: { permission: PERMISSIONS.dashboard.view },
      },
      {
        path: 'dashboard',
        loadChildren: () => import('./home/home.routes'),
        canActivate: [accessGuard],
        data: { permission: PERMISSIONS.dashboard.view },
      },
      {
        path: 'profiel',
        loadChildren: () => import('./users/profile.routes'),
      },
      {
        path: 'gebruikers',
        loadChildren: () => import('./users/users.routes'),
        canActivate: [accessGuard],
        data: { permission: PERMISSIONS.user.view },
      },
      {
        path: 'beheerders',
        loadChildren: () => import('./users/managers.routes'),
        canActivate: [accessGuard],
        data: { permission: PERMISSIONS.user.viewGlobal },
      },
      {
        path: 'configuratie',
        loadChildren: () => import('./configurator/configurator.routes'),
        canActivate: [accessGuard, organizationContextGuard],
        data: {
          permission: PERMISSIONS.configurator.view,
          hierarchyMenu: { type: DashboardType.Configuration, edit: true, flow: true },
          queryParameters: { objects: 'single', flow: true },
        },
      },
      {
        path: 'organisaties',
        loadChildren: () => import('./organizations/organization.routes'),
      },
      {
        path: 'meldingen',
        loadChildren: () => import('./notifications/notifications.routes'),
        canActivate: [accessGuard],
        data: { permission: PERMISSIONS.notification.view },
      },
      {
        path: 'doelen',
        loadChildren: () => import('./targets/targets.routes'),
        canActivate: [accessGuard],
        data: { permission: PERMISSIONS.target.view },
      },
      {
        path: 'contracten',
        loadChildren: () => import('./utility-contracts/utility-contracts.routes'),
        canActivate: [accessGuard],
        data: { permission: PERMISSIONS.utilityContract.view },
      },
      {
        path: 'export',
        loadChildren: () => import('./export/export.routes'),
        canActivate: [accessGuard],
        data: { permission: PERMISSIONS.dashboard.exportData },
      },
      {
        path: 'alarmen',
        loadChildren: () => import('./alarms/alarms.routes'),
        canActivate: [accessGuard],
        data: { permission: PERMISSIONS.alarm.view },
      },
      {
        path: 'milieu',
        loadChildren: () => import('./dashboards/environments.routes'),
        canActivate: [accessGuard],
        data: {
          permission: PERMISSIONS.environment.view,
          feature: FEATURES.modules.environment.module,
        },
      },
      {
        path: 'klimaat',
        loadChildren: () => import('./dashboards/climate.routes'),
        canActivate: [accessGuard],
        data: {
          permission: PERMISSIONS.climate.view,
          feature: FEATURES.modules.climate.module,
        },
      },
      {
        path: 'alerts',
        loadChildren: () => import('./alerts/alert.routes'),
        canActivate: [accessGuard],
        data: { permission: PERMISSIONS.alert.view },
      },
      {
        path: 'historie',
        loadChildren: () => import('./history/history.routes'),
        canActivate: [accessGuard],
        data: { feature: FEATURES.modules.history.module },
      },
      { path: '**', redirectTo: '' },
    ],
  },
];

interface IRoute extends Route {
  data?: IRouteData;
  children?: AppRoutes;
}

export interface IRouteData extends IAccessGuardData, IHierarchyMenuConfig, IQueryParametersConfig {}

export declare type AppRoutes = IRoute[];

@Injectable()
export class CustomTitleStrategy extends TitleStrategy {
  private readonly title = inject(Title);
  private readonly titleService = inject(TitleService);
  private readonly casePipe = new CasePipe();
  private readonly baseTitle = 'Energiemanager';
  private readonly separator = ' | ';
  private readonly isStandalone = window.matchMedia('(display-mode: standalone)').matches;

  public override updateTitle(routerState: RouterStateSnapshot): void {
    let title =
      this.buildTitle(routerState) ?? this.casePipe.transform(this.getFirstRouteName(routerState.root), 'title');

    if (!title.length) {
      title = this.baseTitle;
    }

    this.titleService.setTitle(title);

    if (!this.isStandalone && title !== this.baseTitle) {
      title += this.separator + this.baseTitle;
    }

    this.title.setTitle(title);
  }

  private getFirstRouteName(route: ActivatedRouteSnapshot): string | undefined {
    while (route.firstChild) {
      route = route.firstChild;
      if (route.url.length) break;
    }
    return route.url.length ? route.url.map((segment) => segment.path).join(' ') : undefined;
  }
}
